import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Trophy, CheckCircle, Phone } from "lucide-react";
import { useState } from "react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  hero: "/gymnastics-group.jpg",
  kids: "/gymnastics-kids.jpg",
  splits: "/gymnastics-splits.jpg",
};

const details = [
  { icon: Users, label: "Yaş Grubu", value: "5-12 Yaş" },
  { icon: Trophy, label: "Seviye", value: "Başlangıç - İleri" },
];

const highlights = [
  "Temel cimnastik hareketleri ve teknikleri",
  "Esneklik ve koordinasyon çalışması",
  "Yer hareketleri ve aletli cimnastik",
  "Güç ve denge eğitimi",
  "Güvenli ve kontrollü ortamda çalışma",
  "Ritim ve müzik eşliğinde egzersizler",
  "Akrobasi temelleri",
  "Bireysel gelişim takibi",
];


const trainingProgram = [
  { level: "Başlangıç", ages: "5-7 Yaş", desc: "Temel esneklik, denge, yuvarlanma ve basit akrobasi hareketleri ile cimnastiğe eğlenceli giriş.", color: "bg-gold" },
  { level: "Gelişim", ages: "8-10 Yaş", desc: "İleri esneklik, köprü, amut, yıldız gibi temel hareketler ve alet üzerinde çalışmalar.", color: "bg-burgundy" },
  { level: "İleri", ages: "11-12 Yaş", desc: "Flip, rondat, çift takla gibi ileri hareketler, koreografi çalışmaları ve gösteri hazırlığı.", color: "bg-[#1A1A1A]" },
];

const galleryItems = [
  { img: IMAGES.hero, title: "Grup Çalışması", span: "md:col-span-2" },
  { img: IMAGES.splits, title: "Esneklik Çalışması" },
  { img: IMAGES.kids, title: "Minik Sporcularımız", span: "md:col-span-2" },
  { img: IMAGES.hero, title: "Antrenman Ortamı" },
];

export default function GymnasticsPage() {
  const [lightboxImg, setLightboxImg] = useState(null);

  return (
    <div data-testid="gymnastics-page" className="pt-20">
      <SEOHead
        title={"Cimnastik Kursu Ataşehir, Kadıköy, Maltepe - Profesyonel Eğitim"}
        description={"İRÜ Spor Kulübü cimnastik kursu. Ataşehir, Kadıköy, Maltepe bölgesinde profesyonel cimnastik eğitimi. 5-12 yaş grubu, deneyimli antrenörler. Anadolu yakası cimnastik spor okulu."}
        keywords={"cimnastik kursu Kadıköy, cimnastik eğitimi Ataşehir, cimnastik spor okulu Maltepe, Anadolu yakası cimnastik, çocuk cimnastik kursu İstanbul, cimnastik antrenmanı, çocuk cimnastik, Bostancı cimnastik, cimnastik okulu İstanbul, artistik cimnastik, ritmik cimnastik"}
        canonical="/branslarimiz/cimnastik"
        ogImage="/gymnastics-group.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Cimnastik Kursu - İRÜ Spor Kulübü",
          "description": "Ataşehir, Kadıköy, Maltepe bölgesinde profesyonel cimnastik eğitimi. 5-12 yaş grubu için deneyimli antrenörler eşliğinde cimnastik kursu.",
          "provider": { "@type": "SportsOrganization", "name": "İRÜ Spor Kulübü" },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "onsite",
            "courseSchedule": { "@type": "Schedule", "repeatFrequency": "P1W", "repeatCount": 2, "duration": "PT60M" },
            "location": {
              "@type": "Place",
              "name": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi",
              "address": { "@type": "PostalAddress", "addressLocality": "İstanbul", "addressRegion": "Kadıköy", "addressCountry": "TR" }
            }
          },
          "audience": { "@type": "EducationalAudience", "educationalRole": "student", "requiredMinAge": 5, "requiredMaxAge": 12 }
        }}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Cimnastik" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Link to="/branslarimiz" className="inline-flex items-center gap-1 text-xs font-accent uppercase tracking-widest text-white/60 hover:text-white/80 transition-colors mb-4">
            {"Branşlarımız"} <span className="mx-1">/</span>
          </Link>
          <div className="flex items-center gap-5 mb-4">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">
                Cimnastik
              </h1>
              <p className="text-xs sm:text-sm font-accent uppercase tracking-widest text-gold mt-1">{"Profesyonel Eğitim Programı"}</p>
            </div>
          </div>
          <p className="mt-4 text-base md:text-lg text-white/70 max-w-lg leading-relaxed">
            {"Esneklik, güç, koordinasyon ve zarafet. Çocuklarınızın motor becerilerini geliştiren en temel spor dalı."}
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            {details.map((d) => (
              <div key={d.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2">
                <d.icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <div>
                  <span className="text-[10px] text-white/50 font-accent uppercase tracking-wider">{d.label}</span>
                  <p className="text-sm text-white/90 font-medium">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/on-kayit">
              <Button data-testid="gym-register-btn" className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-6">
                {"Kayıt Ol"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="gym-info-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hakkında */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Branş Detayı"}</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                {"Ataşehir, Kadıköy, Maltepe Cimnastik Kursu"}
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                {"İRÜ Spor Kulübü cimnastik branşında, Ataşehir, Kadıköy ve Maltepe bölgesinden gelen çocukların motor becerilerini, esnekliklerini ve koordinasyonlarını geliştirmeye yönelik kapsamlı eğitim programları uygulanmaktadır. Güvenlik her zaman öncelikli önceliğimizdir."}
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                {"Cimnastik, tüm sporların temelini oluşturan beceriler kazandırır. İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'ndeki modern tesislerimizde çocuklar vücut farkındalığı, esneklik, güç ve özgüven kazanır. Eğitimli antrenörlerimiz güvenli bir ortamda her çocuğun kendi hızında ilerlemesini sağlar."}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-burgundy mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-gray-600">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={IMAGES.splits} alt="Cimnastik spagat" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border-2 border-burgundy/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Eğitim Seviyeleri */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Eğitim Programı"}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            {"Seviyeye Göre Programlar"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trainingProgram.map((p) => (
              <div key={p.level} className="border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 overflow-hidden bg-white">
                <div className={`${p.color} px-6 py-4 flex items-center justify-between`}>
                  <span className="font-heading text-base font-bold uppercase tracking-wider text-white">{p.level}</span>
                  <span className="font-accent text-xs text-white/80 bg-white/20 px-3 py-1">{p.ages}</span>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeri */}
      <section className="py-20 lg:py-28 bg-white" data-testid="gymnastics-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Galeri</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Cimnastikten Kareler
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[280px]">
            {galleryItems.map((item, idx) => (
              <div
                key={`${item.title}-${idx}`}
                className={`relative group overflow-hidden cursor-pointer ${item.span || ""}`}
                onClick={() => setLightboxImg(item.img)}
                data-testid={`gallery-item-${idx}`}
              >
                <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <p className="font-heading text-sm uppercase tracking-wider text-white">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
          data-testid="gallery-lightbox"
        >
          <img src={lightboxImg} alt="Galeri" className="max-w-full max-h-[85vh] object-contain" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl font-light">&times;</button>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-burgundy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">{"Cimnastik Ailemize Katılın"}</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            {"Çocuklarınızın motor becerilerini, esnekliğini ve koordinasyonunu geliştiren eğlenceli cimnastik programlarımıza katılın."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/on-kayit">
              <Button data-testid="gym-cta-register" className="bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-10 py-6">
                {"Ön Kayıt Formu"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="gym-cta-info" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-10 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
